import {ReactElement, useEffect, useRef, useState} from "react";
import {
    GetPmsAverageMarektRentsQuery,
    OriginalRevenueMarketRentModeType,
    UnitTypeScheduleValueUpsertInput,
    UnitTypeWithOriginalrevenueMarketRentOverridesGranularInput,
    UpdateOriginalRevenueMarketRentDataMutationVariables,
    useGetPmsAverageMarektRentsQuery,
    useUpdateOriginalRevenueMarketRentDataMutation,
    VersionType
} from "../../../../__generated__/generated_types";
import {toast} from "react-toastify";
import {Property} from "../../../../contexts/properties/logic";
import {UnitTypeSyncWithPMSTable} from "../shared-sync-with-pms/UnitTypeSyncWithPMSTable";
import {useSaveUnitTypeScheduleValues} from "../base";
import {TUnitTypeData} from "../shared-sync-with-pms/logic";


export interface AverageRentRewriteViewProps {
    property: Property,
    budgetYear: number,
    marketRentMode: OriginalRevenueMarketRentModeType,
    valuesStartFromIndex: number,
    unitTypes: {id: string, type: string;}[];
    versionType: VersionType,
    onCancel: () => void,
    onApply: () => void;
}

function toFixed(val: number | null): number | null {
    if(val == null) {
        return null;
    }
    return Math.round(val);
}

function buildUnitTypesData(
    rawData: GetPmsAverageMarektRentsQuery,
    valuesStartFromIndex: number,
    unitTypes: {id: string, type: string;}[],
    versionType: VersionType,
    marketRentMode: OriginalRevenueMarketRentModeType
): TUnitTypeData[] {
    const forecastedValuesByUnitType: Record<string, (number | null)[]> = {};
    if(marketRentMode == OriginalRevenueMarketRentModeType.Manual) {
        for(const utsv of rawData.valueVersions.find(vv => vv.type == versionType)?.unitTypeScheduleValues ?? []) {
            let utValuesSoFar = forecastedValuesByUnitType[utsv.unitTypeId];
            if(!utValuesSoFar) {
                utValuesSoFar = new Array<number | null>(12).fill(null);
                forecastedValuesByUnitType[utsv.unitTypeId] = utValuesSoFar;
            }
            const val = parseFloat(utsv.averageMarketRent);
            if(isNaN(val)) {
                utValuesSoFar[utsv.monthIndex] = null;
            }
            else {
                utValuesSoFar[utsv.monthIndex] = val;
            }
        }
    }
    else {
        for(const utData of rawData.originalRevenueMarketRentValues) {
            if(versionType == VersionType.Reforecast) {
                const utValues = utData.reforecast.map(v => v == undefined || v == null ? null : v);
                forecastedValuesByUnitType[utData.unitTypeId] = utValues;
            }
            else {
                const utValues = utData.budget.map(v => v == undefined || v == null ? null : v);
                forecastedValuesByUnitType[utData.unitTypeId] = utValues;
            }
        }

    }
    const pmsAverageMarketRentsByUnitTypeId = rawData.queryPMSAverageMarektRentsForPropertyAndYears.toIdMap("unitTypeId");
    const ret = [] as TUnitTypeData[];
    for(const unitType of unitTypes) {
        const pmsAverageMarketRents = pmsAverageMarketRentsByUnitTypeId[unitType.id];
        const pmsValues = new Array<number | null>(12).fill(null);
        if(pmsAverageMarketRents) {
            for(let i = valuesStartFromIndex; i < 12; i++) {
                const averageMarketRent = pmsAverageMarketRents.values[i];
                const val = parseFloat(averageMarketRent);
                if(isNaN(val)) {
                    pmsValues[i] = null;
                }
                else {
                    pmsValues[i] = toFixed(val);
                }
            }
        }
        const forecastedValues = new Array<number | null>(12).fill(null);
        const utForecastedValues = forecastedValuesByUnitType[unitType.id];
        if(utForecastedValues) {
            for(let i = valuesStartFromIndex; i < 12; i++) {
                const utValue = utForecastedValues[i];
                if(utValue !== null && utValue !== undefined) {
                    forecastedValues[i] = toFixed(utValue);
                }
            }
        }
        ret.push({
            unitTypeId: unitType.id,
            unitTypeName: unitType.type,
            pmsValues: [...pmsValues],
            currentForecastedValues: [...forecastedValues],
            newForecastedValues: [...pmsValues],
            useNewForecasedValues: true
        });
    }

    return ret;
}

export function AverageRentRewriteView(props: AverageRentRewriteViewProps): ReactElement {
    const {data: averageRentsRawData, loading: occupiedCountsRawLoading} = useGetPmsAverageMarektRentsQuery({
        variables: {
            propertyId: props.property.id,
            propertyIdStr: props.property.id,
            pmsDataYears: [props.versionType == VersionType.Reforecast ? props.property.reforecastYear : props.property.budgetYear],
            budgetYear: props.property.budgetYear
        },
        fetchPolicy: "no-cache"
    });
    const saveChangesManual = useSaveUnitTypeScheduleValues(props.property.id, "averageMarketRent", props.budgetYear);
    const [saveChangesGrowth] = useUpdateOriginalRevenueMarketRentDataMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if(data?.setOriginalRevenueMarketRentAdjustmentsGranular === false || data?.setOriginalRevenueMarketRentOverridesGranular === false) {
                toast.error(`Failed To Update`);
            }
            isSaving.current = true;
            props.onApply();
        },
    });
    const isSaving = useRef(false);

    const [unitTypesData, setUnitTypesData] = useState<TUnitTypeData[]>();

    useEffect(() => {
        if(!averageRentsRawData || occupiedCountsRawLoading) {
            return;
        }

        const unitTypesData = buildUnitTypesData(averageRentsRawData, props.valuesStartFromIndex, props.unitTypes, props.versionType, props.marketRentMode);
        setUnitTypesData(unitTypesData.sortBy("unitTypeName"));
    }, [averageRentsRawData, occupiedCountsRawLoading]);

    function handleApply(updates: {
        unitTypeId: string;
        values: (number | null | undefined)[];
    }[]) {
        if(props.marketRentMode === "MANUAL") {
            if(!averageRentsRawData || !unitTypesData) {
                return;
            }
            const unitTypeNameById = unitTypesData.toIdMap("unitTypeId", "unitTypeName");
            const versionId = averageRentsRawData.valueVersions.find(v => v.type == props.versionType)?.id;
            if(!versionId) {
                return;
            }
            const values: UnitTypeScheduleValueUpsertInput[] = [];
            for(const update of updates) {
                const utName = unitTypeNameById[update.unitTypeId];
                if(!utName) {
                    continue;
                }
                update.values.forEach((val, monthIndex) => {
                    if(val !== undefined && val !== null) {
                        values.push({monthIndex, unitTypeName: utName, value: val.toString()});
                    }
                });
            }
            isSaving.current = true;
            saveChangesManual(
                versionId,
                values
            )
                .then(() => toast.success(`Updated Successfully`))
                .catch(() => toast.error(`Failed To Update`))
                .finally(() => {isSaving.current = true; props.onApply();});
        }
        else {
            const unitTypeOverrides = [];
            const variables: UpdateOriginalRevenueMarketRentDataMutationVariables = {
                propertyId: props.property.id,
                budgetYear: props.property.budgetYear,
                versionType: props.versionType,
                unitTypeOverrides: []
            };
            for(const {unitTypeId, values: overrideValues} of updates) {
                const input: UnitTypeWithOriginalrevenueMarketRentOverridesGranularInput = {
                    unitTypeId: unitTypeId,
                    ovM0: overrideValues[0],
                    ovM1: overrideValues[1],
                    ovM2: overrideValues[2],
                    ovM3: overrideValues[3],
                    ovM4: overrideValues[4],
                    ovM5: overrideValues[5],
                    ovM6: overrideValues[6],
                    ovM7: overrideValues[7],
                    ovM8: overrideValues[8],
                    ovM9: overrideValues[9],
                    ovM10: overrideValues[10],
                    ovM11: overrideValues[11],
                };
                unitTypeOverrides.push(input);
            }
            variables.unitTypeOverrides = unitTypeOverrides;
            isSaving.current = true;
            saveChangesGrowth({
                variables: variables
            });
        }
    }

    return (
        <UnitTypeSyncWithPMSTable
            data={unitTypesData}
            valuesStartFromIndex={props.valuesStartFromIndex}
            isApplying={isSaving.current}
            onCancel={props.onCancel}
            onApply={handleApply}
        />
    );
}